import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { fetchCities } from '../api/citiesApi';
import { fetchLanguagesConfig } from '../api/languagesApi';
import { useAuth } from '../hooks/useAuth';

import styles from './CityPickerPage.module.css';

const LAST_SELECTION_KEY = 'radiri-last-selection';

interface SavedSelection {
  lang: string;
  citySlug: string;
}

function getSavedSelection(): SavedSelection | null {
  try {
    const raw = localStorage.getItem(LAST_SELECTION_KEY);
    return raw ? (JSON.parse(raw) as SavedSelection) : null;
  } catch {
    return null;
  }
}

export default function CityPickerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUserPreferences } = useAuth();

  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [selectedLang, setSelectedLang] = useState<string | null>(null);

  const { data: cities, isLoading: citiesLoading, isError: citiesError } = useQuery({
    queryKey: ['cities'],
    queryFn: () => fetchCities(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: allLanguages, isLoading: langsLoading } = useQuery({
    queryKey: ['languages-config'],
    queryFn: fetchLanguagesConfig,
    staleTime: 60 * 60 * 1000,
  });

  // Languages that appear in at least one city's output_languages
  const availableLanguageCodes = new Set<string>(
    (cities ?? []).flatMap((c) => c.output_languages),
  );
  const displayLanguages = (allLanguages ?? []).filter((l) =>
    availableLanguageCodes.has(l.code),
  );

  // Auto-redirect to last-used selection on fresh visits.
  const intentional = (location.state as { intentional?: boolean } | null)?.intentional ?? false;
  useEffect(() => {
    if (intentional) return;
    if (!cities || !availableLanguageCodes.size) return;
    const saved = getSavedSelection();
    if (!saved) return;
    const matchingCity = cities.find((c) => c.slug === saved.citySlug);
    if (matchingCity && matchingCity.output_languages.includes(saved.lang)) {
      navigate(`/${saved.lang}/${saved.citySlug}/`, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cities, intentional]);

  function handleGo() {
    if (!selectedCity || !selectedLang) return;
    try {
      localStorage.setItem(
        LAST_SELECTION_KEY,
        JSON.stringify({ lang: selectedLang, citySlug: selectedCity }),
      );
    } catch { /* non-fatal */ }
    if (user) {
      void updateUserPreferences({ city_slug: selectedCity, news_language: selectedLang }).catch(() => {});
    }
    navigate(`/${selectedLang}/${selectedCity}/`);
  }

  const isLoading = citiesLoading || langsLoading;

  if (isLoading) return (
    <div className={styles.page}>
      <p className={styles.loading}>{t('loading')}</p>
    </div>
  );
  if (citiesError) return (
    <div className={styles.page}>
      <p className={styles.error}>{t('error_loading')}</p>
    </div>
  );

  const goEnabled =
    selectedCity !== null &&
    selectedLang !== null &&
    (cities?.find((c) => c.slug === selectedCity)?.output_languages.includes(selectedLang) ?? false);

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t('app_name')}</h1>
      <p className={styles.subtitle}>{t('select_city_and_language')}</p>

      <div className={styles.columns}>
        {/* ── City list ──────────────────────────────────────────────── */}
        <div className={styles.column}>
          <h2 className={styles.columnTitle}>{t('city')}</h2>
          <ul className={styles.list}>
            {(cities ?? []).map((city) => {
              const available =
                selectedLang === null || city.output_languages.includes(selectedLang);
              const tooltip = available
                ? undefined
                : t('city_unavailable_tooltip', {
                    lang: selectedLang,
                    langs: city.output_languages.join(', '),
                  });
              return (
                <li key={city.slug}>
                  <button
                    type="button"
                    className={`${styles.listItem} ${
                      city.slug === selectedCity ? styles.listItemSelected : ''
                    } ${!available ? styles.listItemDisabled : ''}`}
                    onClick={available ? () => setSelectedCity(city.slug) : undefined}
                    disabled={!available}
                    title={tooltip}
                  >
                    {city.name}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* ── Language list ──────────────────────────────────────────── */}
        <div className={styles.column}>
          <h2 className={styles.columnTitle}>{t('news_language')}</h2>
          <ul className={styles.list}>
            {displayLanguages.map((lang) => {
              const currentCityLangs =
                cities?.find((c) => c.slug === selectedCity)?.output_languages ?? null;
              const available = currentCityLangs === null || currentCityLangs.includes(lang.code);
              const currentCityName =
                cities?.find((c) => c.slug === selectedCity)?.name ?? selectedCity ?? '';
              const tooltip = available
                ? undefined
                : t('language_unavailable_tooltip', {
                    city: currentCityName,
                    langs: currentCityLangs?.join(', ') ?? '',
                  });
              return (
                <li key={lang.code}>
                  <button
                    type="button"
                    className={`${styles.listItem} ${
                      lang.code === selectedLang ? styles.listItemSelected : ''
                    } ${!available ? styles.listItemDisabled : ''}`}
                    onClick={available ? () => setSelectedLang(lang.code) : undefined}
                    disabled={!available}
                    title={tooltip}
                  >
                    {lang.code} {lang.flag}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className={styles.goRow}>
        <button
          type="button"
          className={styles.goBtn}
          onClick={handleGo}
          disabled={!goEnabled}
        >
          {t('go')}
        </button>
      </div>
    </div>
  );
}

